import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import type { PickerPipeline } from "@/lib/stageMovePicker";

/**
 * Every pipeline a project in `currentPipelineId` may move to, with its stages.
 *
 * "May move to" means the same account and the same pipeline type: residential
 * projects never move into commercial pipelines, or the other way round. The
 * account is read off the current pipeline rather than the session, so a
 * project viewed from a parent account is only ever offered its own account's
 * pipelines.
 *
 * Resolves to an empty list when the current pipeline is not readable; callers
 * then fall back to the stages they already hold.
 */
export function usePipelineStageOptions(
  currentPipelineId: number | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: ["pipeline-stage-options", currentPipelineId],
    enabled: enabled && !!currentPipelineId,
    staleTime: 60_000,
    queryFn: async (): Promise<PickerPipeline[]> => {
      const { data: current, error: currentError } = await supabase
        .from("pipelines")
        .select("id, pipeline_type, tenant_id")
        .eq("id", currentPipelineId)
        .maybeSingle();
      if (currentError) throw currentError;
      if (!current) return [];

      let pipelinesQuery = supabase
        .from("pipelines")
        .select("id, name")
        .eq("tenant_id", current.tenant_id)
        .order("name");
      pipelinesQuery = current.pipeline_type
        ? pipelinesQuery.eq("pipeline_type", current.pipeline_type)
        : pipelinesQuery.eq("id", current.id);
      const { data: pipelines, error: pipelinesError } = await pipelinesQuery;
      if (pipelinesError) throw pipelinesError;
      if (!pipelines?.length) return [];

      const { data: stages, error: stagesError } = await supabase
        .from("stages")
        .select("id, name, pipeline_id, stage_order")
        .in(
          "pipeline_id",
          pipelines.map((p) => p.id)
        )
        .order("stage_order", { ascending: true });
      if (stagesError) throw stagesError;

      return pipelines
        .map((p) => ({
          id: p.id,
          name: p.name,
          stages: (stages ?? [])
            .filter((s) => s.pipeline_id === p.id)
            .map((s) => ({ id: s.id, name: s.name })),
        }))
        .filter((p) => p.stages.length > 0);
    },
  });
}
